import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineGrid,
  InlineStack,
  Layout,
  Page,
  Pagination,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  ARCHIVE_PAGE_SIZE,
  EMPTY_ARCHIVE_CRITERIA,
  exportArchivedDocuments,
  exportEvidencePack,
  getArchivedDocument,
  getArchivedDocumentContent,
  listArchivedDocuments,
  type ArchiveSearchCriteria,
} from "../api/archive";
import { ApiError, type ApiBlobResponse } from "../api/client";
import { currentLocale, t } from "../lib/i18n";
import type { ArchivedDocumentDetail, ArchivedDocumentSummary } from "../types";

interface PreviewState {
  detail: ArchivedDocumentDetail;
  url: string | null;
  canPreview: boolean;
}

/** 電帳法の必須3項目で検索し、保存実体と訂正前の版を取り出す画面。 */
export function Archive() {
  const navigate = useNavigate();
  const location = useLocation();
  const [draft, setDraft] = useState<ArchiveSearchCriteria>({ ...EMPTY_ARCHIVE_CRITERIA });
  const [applied, setApplied] = useState<ArchiveSearchCriteria>({ ...EMPTY_ARCHIVE_CRITERIA });
  const [offset, setOffset] = useState(0);
  const [searchRevision, setSearchRevision] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyDocumentId, setBusyDocumentId] = useState<number | null>(null);
  const [exporting, setExporting] = useState<"csv" | "zip" | "evidence" | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const previewUrl = useRef<string | null>(null);
  const previewRequestRevision = useRef(0);
  const verifyRequestRevision = useRef(0);
  const paginationLocked = useRef(false);
  const actionErrorRegion = useRef<HTMLDivElement | null>(null);
  const previewRegion = useRef<HTMLDivElement | null>(null);

  const query = useQuery({
    queryKey: ["archive-documents", applied, offset, searchRevision],
    queryFn: () => listArchivedDocuments(applied, offset),
    placeholderData: (previous) => previous,
  });

  useEffect(
    () => () => {
      // アンマウント後に完了したリクエストが state や Object URL を作らないよう無効化する。
      previewRequestRevision.current += 1;
      verifyRequestRevision.current += 1;
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    },
    [],
  );

  useEffect(() => {
    if (!query.isFetching) paginationLocked.current = false;
  }, [query.isFetching]);

  useEffect(() => {
    if (!actionError) return;
    actionErrorRegion.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    actionErrorRegion.current?.focus({ preventScroll: true });
  }, [actionError]);

  useEffect(() => {
    if (!preview) return;
    previewRegion.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    previewRegion.current?.focus({ preventScroll: true });
  }, [preview]);

  function update<K extends keyof ArchiveSearchCriteria>(
    key: K,
    value: ArchiveSearchCriteria[K],
  ) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function applySearch(event: FormEvent) {
    event.preventDefault();
    setActionError(null);
    setOffset(0);
    setApplied({ ...draft });
    // 同じ条件での再検索も明示的に実行する。
    setSearchRevision((value) => value + 1);
  }

  function clearSearch() {
    setDraft({ ...EMPTY_ARCHIVE_CRITERIA });
    setApplied({ ...EMPTY_ARCHIVE_CRITERIA });
    setOffset(0);
    setSearchRevision((value) => value + 1);
    setActionError(null);
  }

  async function openPreview(documentId: number) {
    const requestRevision = ++previewRequestRevision.current;
    // 表示対象が変わり始めた時点で、旧書類に対する改ざん検知を無効化する。
    verifyRequestRevision.current += 1;
    setIsVerifying(false);
    setBusyDocumentId(documentId);
    setActionError(null);
    try {
      const [detail, file] = await Promise.all([
        getArchivedDocument(documentId),
        getArchivedDocumentContent(documentId),
      ]);
      if (requestRevision !== previewRequestRevision.current) return;

      const canPreview = isSafePreviewType(file.blob.type || detail.document.content_type);
      const url = canPreview ? URL.createObjectURL(file.blob) : null;
      replacePreviewUrl(url);
      setPreview({ detail, url, canPreview });
    } catch (error) {
      if (requestRevision === previewRequestRevision.current) {
        setActionError(errorMessage(error));
      }
    } finally {
      if (requestRevision === previewRequestRevision.current) {
        setBusyDocumentId(null);
      }
    }
  }

  async function verifyPreview() {
    if (!preview) return;
    const documentId = preview.detail.document.id;
    const requestRevision = ++verifyRequestRevision.current;
    setIsVerifying(true);
    setActionError(null);
    try {
      const detail = await getArchivedDocument(documentId, true);
      if (requestRevision !== verifyRequestRevision.current) return;

      setPreview((current) =>
        current?.detail.document.id === documentId ? { ...current, detail } : current,
      );
    } catch (error) {
      if (requestRevision === verifyRequestRevision.current) {
        setActionError(errorMessage(error));
      }
    } finally {
      if (requestRevision === verifyRequestRevision.current) {
        setIsVerifying(false);
      }
    }
  }

  async function downloadDocument(document: ArchivedDocumentSummary) {
    setBusyDocumentId(document.id);
    setActionError(null);
    try {
      const file = await getArchivedDocumentContent(document.id, true);
      saveBlob(file, fallbackDocumentName(document));
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setBusyDocumentId(null);
    }
  }

  async function exportDocuments(format: "csv" | "zip") {
    if (criteriaDirty || query.isFetching) {
      setActionError(t("変更した検索条件を反映してから出力してください"));
      return;
    }
    setExporting(format);
    setActionError(null);
    try {
      const file = await exportArchivedDocuments(format, applied);
      saveBlob(file, format === "csv" ? "denchobox-index.csv" : "denchobox-archive.zip");
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setExporting(null);
    }
  }

  async function downloadEvidencePack() {
    setExporting("evidence");
    setActionError(null);
    try {
      const file = await exportEvidencePack();
      saveBlob(file, "denchobox-evidence-pack.zip");
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setExporting(null);
    }
  }

  function closePreview() {
    previewRequestRevision.current += 1;
    verifyRequestRevision.current += 1;
    setBusyDocumentId(null);
    setIsVerifying(false);
    replacePreviewUrl(null);
    setPreview(null);
  }

  function replacePreviewUrl(next: string | null) {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = next;
  }

  const total = query.data?.total ?? 0;
  const criteriaDirty = (Object.keys(draft) as Array<keyof ArchiveSearchCriteria>).some(
    (key) => draft[key] !== applied[key],
  );
  const hasPrevious = offset > 0;
  const hasNext = offset + ARCHIVE_PAGE_SIZE < total;
  const lastPageOffset = total > 0 ? Math.floor((total - 1) / ARCHIVE_PAGE_SIZE) * ARCHIVE_PAGE_SIZE : 0;

  function goToPreviousPage() {
    if (paginationLocked.current || query.isFetching) return;
    paginationLocked.current = true;
    setOffset((value) => Math.max(0, value - ARCHIVE_PAGE_SIZE));
  }

  function goToNextPage() {
    if (paginationLocked.current || query.isFetching) return;
    paginationLocked.current = true;
    setOffset((value) => Math.min(value + ARCHIVE_PAGE_SIZE, lastPageOffset));
  }

  return (
    <Page
      title={t("アーカイブ検索")}
      subtitle={t("取引年月日・取引金額・取引先名を組み合わせて検索できます")}
      backAction={{
        content: t("ホーム"),
        onAction: () => navigate({ pathname: "/", search: location.search }),
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {actionError ? (
              <div ref={actionErrorRegion} tabIndex={-1}>
                <Banner tone="critical" title={t("処理に失敗しました")} onDismiss={() => setActionError(null)}>
                  <Text as="p">{actionError}</Text>
                </Banner>
              </div>
            ) : null}

            <Card>
              <form onSubmit={applySearch}>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    {t("検索条件")}
                  </Text>
                  <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                    <TextField
                      label={t("取引年月日（開始）")}
                      type="date"
                      autoComplete="off"
                      value={draft.date_from}
                      onChange={(value) => update("date_from", value)}
                    />
                    <TextField
                      label={t("取引年月日（終了）")}
                      type="date"
                      autoComplete="off"
                      value={draft.date_to}
                      onChange={(value) => update("date_to", value)}
                    />
                    <TextField
                      label={t("取引金額（下限）")}
                      type="number"
                      min={0}
                      step={0.01}
                      autoComplete="off"
                      value={draft.amount_min}
                      onChange={(value) => update("amount_min", value)}
                    />
                    <TextField
                      label={t("取引金額（上限）")}
                      type="number"
                      min={0}
                      step={0.01}
                      autoComplete="off"
                      value={draft.amount_max}
                      onChange={(value) => update("amount_max", value)}
                    />
                  </InlineGrid>
                  <TextField
                    label={t("取引先名")}
                    helpText={t("部分一致で検索します")}
                    autoComplete="off"
                    maxLength={255}
                    value={draft.counterparty}
                    onChange={(value) => update("counterparty", value)}
                  />
                  <InlineStack gap="200">
                    <Button variant="primary" submit loading={query.isFetching}>
                      {t("検索")}
                    </Button>
                    <Button onClick={clearSearch}>{t("条件をクリア")}</Button>
                  </InlineStack>
                </BlockStack>
              </form>
            </Card>

            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                  <BlockStack gap="100">
                    <Text as="h2" variant="headingMd">
                      {t("検索結果")}
                    </Text>
                    <Text as="p" tone="subdued">
                      {t("{count}件が見つかりました", { count: total })}
                    </Text>
                  </BlockStack>
                  <InlineStack gap="200">
                    <Button
                      onClick={() => void exportDocuments("csv")}
                      loading={exporting === "csv"}
                      disabled={exporting !== null || total === 0 || criteriaDirty || query.isFetching}
                    >
                      {t("CSV索引簿")}
                    </Button>
                    <Button
                      onClick={() => void exportDocuments("zip")}
                      loading={exporting === "zip"}
                      disabled={exporting !== null || total === 0 || criteriaDirty || query.isFetching}
                    >
                      {t("一括ZIP")}
                    </Button>
                    <Button
                      onClick={() => void downloadEvidencePack()}
                      loading={exporting === "evidence"}
                      disabled={exporting !== null || query.isFetching}
                    >
                      {t("証跡パック（Pro）")}
                    </Button>
                  </InlineStack>
                </InlineStack>
                <Text as="p" tone="subdued">
                  {t("証跡パックには訂正前を含む全版・監査履歴・整合性検証が入ります")}
                </Text>
                {criteriaDirty ? (
                  <Text as="p" tone="caution">
                    {t("変更した検索条件は、検索ボタンで反映してから出力できます")}
                  </Text>
                ) : null}

                {query.isPending ? (
                  <SkeletonBodyText lines={8} />
                ) : query.isError ? (
                  <Banner
                    tone="critical"
                    title={t("検索結果を読み込めませんでした")}
                    action={{ content: t("再読み込み"), onAction: () => void query.refetch() }}
                  >
                    <Text as="p">{errorMessage(query.error)}</Text>
                  </Banner>
                ) : (
                  <>
                    {query.data.documents.length === 0 ? (
                      <Text as="p" tone="subdued">
                        {t("条件に一致する書類はありません")}
                      </Text>
                    ) : (
                      <ArchiveTable
                        documents={query.data.documents}
                        busyDocumentId={busyDocumentId}
                        onPreview={(id) => void openPreview(id)}
                        onDownload={(document) => void downloadDocument(document)}
                        onIssue={(documentId) =>
                          navigate({
                            pathname: `/invoices/${documentId}`,
                            search: location.search,
                          })
                        }
                      />
                    )}
                    {hasPrevious || hasNext ? (
                      <InlineStack align="center">
                        <Pagination
                          hasPrevious={hasPrevious && !query.isFetching}
                          onPrevious={goToPreviousPage}
                          hasNext={hasNext && !query.isFetching}
                          onNext={goToNextPage}
                          label={t("{from}〜{to}件 / 全{total}件", {
                            from: total === 0 ? 0 : Math.min(offset + 1, total),
                            to: Math.min(offset + ARCHIVE_PAGE_SIZE, total),
                            total,
                          })}
                        />
                      </InlineStack>
                    ) : null}
                  </>
                )}
              </BlockStack>
            </Card>

            {preview ? (
              <div ref={previewRegion} tabIndex={-1}>
                <PreviewCard
                  preview={preview}
                  busyDocumentId={busyDocumentId}
                  isVerifying={isVerifying}
                  onClose={closePreview}
                  onVerify={() => void verifyPreview()}
                  onSelectVersion={(id) => void openPreview(id)}
                  onDownload={(document) => void downloadDocument(document)}
                />
              </div>
            ) : null}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

interface ArchiveTableProps {
  documents: ArchivedDocumentSummary[];
  busyDocumentId: number | null;
  onPreview: (documentId: number) => void;
  onDownload: (document: ArchivedDocumentSummary) => void;
  onIssue: (documentId: number) => void;
}

function ArchiveTable({ documents, busyDocumentId, onPreview, onDownload, onIssue }: ArchiveTableProps) {
  return (
    <DataTable
      columnContentTypes={["text", "text", "text", "numeric", "text", "text"]}
      headings={[
        t("取引年月日"),
        t("取引先名"),
        t("書類"),
        t("取引金額"),
        t("状態"),
        t("操作"),
      ]}
      rows={documents.map((document) => [
        document.transaction_date,
        document.counterparty_name,
        document.title || document.external_id || `#${document.id}`,
        `${document.currency} ${formatExactAmount(document.total_amount)}`,
        document.is_live ? <Badge tone="success">{t("現行版")}</Badge> : <Badge>{t("旧版")}</Badge>,
        <InlineStack gap="200" wrap={false}>
          <Button
            size="slim"
            onClick={() => onPreview(document.id)}
            loading={busyDocumentId === document.id}
          >
            {t("プレビュー")}
          </Button>
          <Button
            size="slim"
            onClick={() => onDownload(document)}
            disabled={busyDocumentId !== null}
          >
            {t("ダウンロード")}
          </Button>
          {document.document_type === "order" && document.is_live ? (
            <Button
              size="slim"
              variant="primary"
              onClick={() => onIssue(document.id)}
              disabled={busyDocumentId !== null}
            >
              {t("帳票を作成")}
            </Button>
          ) : null}
        </InlineStack>,
      ])}
    />
  );
}

interface PreviewCardProps {
  preview: PreviewState;
  busyDocumentId: number | null;
  isVerifying: boolean;
  onClose: () => void;
  onVerify: () => void;
  onSelectVersion: (documentId: number) => void;
  onDownload: (document: ArchivedDocumentSummary) => void;
}

function PreviewCard({
  preview,
  busyDocumentId,
  isVerifying,
  onClose,
  onVerify,
  onSelectVersion,
  onDownload,
}: PreviewCardProps) {
  const { document, versions, integrity } = preview.detail;
  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center" gap="300">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              {document.title || document.external_id || `#${document.id}`}
            </Text>
            <Text as="p" tone="subdued">
              {t("保存日時: {date}", { date: formatDateTime(document.archived_at) })}
            </Text>
          </BlockStack>
          <InlineStack gap="200">
            <Button onClick={onVerify} loading={isVerifying}>
              {t("改ざん検知")}
            </Button>
            <Button onClick={() => onDownload(document)} disabled={busyDocumentId !== null}>
              {t("ダウンロード")}
            </Button>
            <Button onClick={onClose}>{t("閉じる")}</Button>
          </InlineStack>
        </InlineStack>

        {integrity ? (
          <Banner
            tone={integrity.is_intact ? "success" : "critical"}
            title={integrity.is_intact ? t("保存時の内容と一致しています") : t("保存後の変更を検知しました")}
          >
            {!integrity.is_intact ? (
              <Text as="p">
                {t("保存時: {expected} / 現在: {actual}", {
                  expected: integrity.expected_sha256,
                  actual: integrity.actual_sha256,
                })}
              </Text>
            ) : null}
          </Banner>
        ) : null}

        {preview.canPreview && preview.url ? (
          <iframe
            title={t("書類プレビュー")}
            src={preview.url}
            sandbox=""
            style={{ width: "100%", minHeight: "440px", border: "1px solid #dfe3e8", borderRadius: 8 }}
          />
        ) : (
          <Banner tone="info" title={t("この形式はブラウザ内で表示できません")}>
            <Text as="p">{t("ダウンロードして内容を確認してください")}</Text>
          </Banner>
        )}

        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            {t("版の履歴（新しい順）")}
          </Text>
          <DataTable
            columnContentTypes={["text", "text", "text", "text"]}
            headings={[t("保存日時"), t("取引年月日"), t("状態"), t("操作")]}
            rows={versions.map((version) => [
              formatDateTime(version.archived_at),
              version.transaction_date,
              version.is_live ? <Badge tone="success">{t("現行版")}</Badge> : <Badge>{t("旧版")}</Badge>,
              <InlineStack gap="200" wrap={false}>
                <Button
                  size="slim"
                  onClick={() => onSelectVersion(version.id)}
                  loading={busyDocumentId === version.id}
                  disabled={version.id === document.id}
                >
                  {version.id === document.id ? t("表示中") : t("表示")}
                </Button>
                <Button size="slim" onClick={() => onDownload(version)} disabled={busyDocumentId !== null}>
                  {t("DL")}
                </Button>
              </InlineStack>,
            ])}
          />
        </BlockStack>
      </BlockStack>
    </Card>
  );
}

function isSafePreviewType(contentType: string): boolean {
  const normalized = contentType.split(";", 1)[0].trim().toLowerCase();
  return normalized === "application/pdf" || normalized === "application/json" || normalized === "text/plain";
}

function fallbackDocumentName(document: ArchivedDocumentSummary): string {
  const extension = document.content_type.includes("pdf") ? "pdf" : "json";
  return `denchobox-${document.transaction_date}-${document.id}.${extension}`;
}

function saveBlob(file: ApiBlobResponse, fallbackName: string): void {
  const url = URL.createObjectURL(file.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = file.filename || fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function formatExactAmount(value: string): string {
  const [integer, decimal] = value.split(".", 2);
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimal === undefined ? grouped : `${grouped}.${decimal}`;
}

function formatDateTime(value: string): string {
  const locale = currentLocale() === "en" ? "en-US" : "ja-JP";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  return error instanceof Error ? error.message : String(error);
}
